<template>
  <b-modal
    v-if="!isMobile"
    id="modal-topic"
    hide-header
    hide-footer
    centered
    scrollable
    no-close-on-backdrop
    size="xl"
    @show="resetParam"
    body-class="d-flex flex-column position-relative modal-body"
  >
    <template #default="{ close }">
      <div
        @dragover.prevent="drag = true"
        @dragleave.prevent="drag = false"
        v-on:drop.prevent="drag = false"
      >
        <drag-area-overlay :drag="drag" v-on:dropFiles="dropFiles" />
        <b-row class="d-flex my-1 ml-0 mr-1 justify-content-between align-items-center">
          <div>{{ navTitle }}</div>
          <div @click="showEditDiscardAlert(close)"><b-icon-x class="x-btn"></b-icon-x></div>
        </b-row>
        <b-row class="d-flex content">
          <b-col lg="6" xl="6">
            <!-- 話題：タイトル -->
            <b-form-group
              id="topic-title-label"
              label="タイトル"
              label-for="topic-title-input"
              label-align="left"
            >
              <b-form-input
                id="topic-title-input"
                placeholder="タイトルを入力してください"
                v-model="titleSource"
                aria-describedby="title-feedback"
                v-on:change="updateComplteButtonDisabled"
                autofocus
                required
                @update="changeEditMode(editMode)"
              />
              <b-form-text
                id="title-feedback"
                :class="validateTitle ? 'success' : 'warning'"
                style="display: flex"
              >
                {{ validTitleMessage }}
              </b-form-text>
            </b-form-group>

            <!-- 話題：説明 -->
            <b-form-group
              id="topic-description-label"
              label="説明"
              label-for="topic-description-input"
              label-align="left"
            >
              <b-form-textarea
                id="topic-description-input"
                rows="7"
                v-model="descSource"
                aria-describedby="desc-feedback"
                placeholder="説明を入力してください"
                v-on:change="updateComplteButtonDisabled"
                @update="changeEditMode(editMode)"
              />
              <b-form-text
                id="desc-feedback"
                :class="validateDesc ? 'success' : 'warning'"
                style="display: flex"
              >
                {{ validDescMessage }}
              </b-form-text>
            </b-form-group>

            <!-- 話題：権限設定-->
            <b-form-group id="topic-acl-label" label="閲覧権限設定">
              <div class="d-flex align-items-center">
                <b-button
                  v-if="isAdmin && allowFeature('acl-dialog')"
                  class="acl-dialog-button"
                  @click="openAclEditModal"
                  >設定画面を開く</b-button
                >
                <div class="ml-2 mb-0">{{ aclStateStr }}</div>
              </div>
            </b-form-group>
          </b-col>

          <b-col lg="6" xl="6">
            <!-- 話題：サムネイルの指定 -->
            <file-selection-form
              ref="fileSelectionForm"
              contentType="TOPIC"
              label="サムネイル"
              :multiple="false"
              :photos="photos"
              :topicId="topicId"
              :allow_attachment_type="allow_attachment_type"
              v-on:onUpdateThumb="updateFile"
            />
            <!-- 話題：カテゴリー -->
            <b-form-group
              id="topic-category-select-one-label"
              label="カテゴリー"
              label-align="left"
            >
              <category-edit
                id="category-edit"
                :categories="categories"
                :isMobile="isMobile"
                :domainId="domainId"
                :me="me"
                :attachedCategories="categorySource ? [categorySource] : []"
                @onCloseCategoryFilterDialog="onCategorySelected"
              />
              <b-button
                v-b-modal.category-edit
                aria-describedby="category-feedback"
                class="category-button"
              >
                <span class="open-icon"></span>
                <span
                  v-if="selectedCategoryColor"
                  :class="`category-color ${selectedCategoryColor}`"
                ></span>
                <div class="category-button-label">{{ selectedCategoryTitle }}</div>
              </b-button>
              <b-form-text
                v-if="!categorySource"
                id="category-feedback"
                class="warning"
                style="display: flex"
              >
                {{ "カテゴリーは必須項目です" }}
              </b-form-text>
            </b-form-group>
            <!-- 話題：固定 -->
            <b-form-group
              id="topic-category-pinned-label"
              label="そのほか"
              label-for="topic-category-pinned"
              label-align="left"
            >
              <b-form-checkbox
                v-if="isNew"
                v-model="notifyOnCreate"
                id="notify-on-create"
                @input="changeEditMode(editMode)"
                >話題の作成時に通知する</b-form-checkbox
              >
              <b-form-checkbox
                v-model="pinnedSource"
                id="topic-category-pinned"
                @input="changeEditMode(editMode)"
                @change="onChangePinnedSource"
                >閲覧者の画面で上部に固定表示する</b-form-checkbox
              >
              <b-form-checkbox
                v-model="pinnedSourceOptions.notification"
                id="topic-notification"
                class="ml-4"
                :disabled="!pinnedSource"
                @input="changeEditMode(editMode)"
                >新規投稿されると全員に通知する</b-form-checkbox
              >
              <b-form-checkbox v-model="allowGuestSource" id="allow-guest-checkbox"
                >ゲストにも閲覧を許可する</b-form-checkbox
              >
              <b-form-checkbox
                v-if="hasFeature('deny-edit')"
                v-model="allowWriteOthersSource"
                id="allow-write-others"
                >作成者以外の投稿(ゲストを除く)・コメント・いいねを許可する</b-form-checkbox
              >
            </b-form-group>
          </b-col>
        </b-row>
        <!-- フッター -->
        <b-row class="mt-2">
          <b-col class="d-flex justify-content-between ml-auto" lg="6" xl="6">
            <cancel
              class="col-5"
              label="キャンセル"
              v-on:cancel="showEditDiscardAlert(onEditCancel)"
            />
            <confirm
              class="col-5"
              label="保存"
              :disabled="completeButtonDisabled"
              v-on:confirm="onEditComplete"
            />
          </b-col>
        </b-row>
      </div>
    </template>
  </b-modal>

  <b-container v-else fluid class="p-0">
    <navigation-bar
      fluid
      :cancel="true"
      @on-cancel="showEditDiscardAlert(onEditCancel)"
      :items="items"
      :title="navTitle"
      :fetchUserInfo="false"
    />

    <b-container class="my-3 topic-edit-body" fluid>
      <!-- 話題：タイトル -->
      <b-form-group
        id="topic-title-label"
        label="タイトル"
        label-for="topic-title-input"
        label-align="left"
      >
        <b-form-input
          id="topic-title-input"
          placeholder="タイトルを入力してください"
          v-model="titleSource"
          aria-describedby="title-feedback"
          v-on:change="updateComplteButtonDisabled"
          autofocus
          required
          @update="changeEditMode(editMode)"
        />
        <b-form-text
          id="title-feedback"
          :class="validateTitle ? 'success' : 'warning'"
          style="display: flex"
        >
          {{ validTitleMessage }}
        </b-form-text>
      </b-form-group>

      <!-- 話題：説明 -->
      <b-form-group
        id="topic-description-label"
        label="説明"
        label-for="topic-description-input"
        label-align="left"
      >
        <b-form-textarea
          id="topic-description-input"
          rows="7"
          v-model="descSource"
          aria-describedby="desc-feedback"
          placeholder="説明を入力してください"
          v-on:change="updateComplteButtonDisabled"
          @update="changeEditMode(editMode)"
        />
        <b-form-text
          id="desc-feedback"
          :class="validateDesc ? 'success' : 'warning'"
          style="display: flex"
        >
          {{ validDescMessage }}
        </b-form-text>
      </b-form-group>

      <!-- 話題：サムネイルの指定 -->
      <file-selection-form
        ref="fileSelectionForm"
        contentType="TOPIC"
        label="サムネイル"
        :multiple="false"
        :photos="photos"
        :topicId="topicId"
        :allow_attachment_type="allow_attachment_type"
        v-on:onUpdateThumb="updateFile"
      />

      <!-- 話題：カテゴリー -->
      <b-form-group
        id="topic-category-select-one-label"
        label="カテゴリー"
        label-for="topic-category-select-one"
        label-align="left"
      >
        <category-edit
          id="category-edit"
          :categories="categories"
          :isMobile="isMobile"
          :domainId="domainId"
          :me="me"
          :attachedCategories="categorySource ? [categorySource] : []"
          @onCloseCategoryFilterDialog="onCategorySelected"
        />
        <b-button
          v-b-modal.category-edit
          aria-describedby="category-feedback"
          class="category-button"
        >
          <span class="open-icon"></span>
          <span
            v-if="selectedCategoryColor"
            :class="`category-color ${selectedCategoryColor}`"
          ></span>
          <div class="category-button-label">{{ selectedCategoryTitle }}</div>
        </b-button>
        <b-form-text
          v-if="!categorySource"
          id="category-feedback"
          class="warning"
          style="display: flex"
        >
          {{ "カテゴリーは必須項目です" }}
        </b-form-text>
      </b-form-group>

      <!-- 話題：固定 -->
      <b-form-group
        id="topic-category-pinned-label"
        label="そのほか"
        label-for="topic-category-pinned"
        label-align="left"
      >
        <b-form-checkbox
          v-if="isNew"
          v-model="notifyOnCreate"
          id="notify-on-create"
          @input="changeEditMode(editMode)"
          >話題の作成時に通知する</b-form-checkbox
        >
        <b-form-checkbox
          v-model="pinnedSource"
          id="topic-category-pinned"
          @input="changeEditMode(editMode)"
          @change="onChangePinnedSource"
          >閲覧者の画面で上部に固定表示する</b-form-checkbox
        >
        <b-form-checkbox
          v-model="pinnedSourceOptions.notification"
          id="topic-notification"
          class="ml-4"
          :disabled="!pinnedSource"
          @input="changeEditMode(editMode)"
          >新規投稿されると全員に通知する</b-form-checkbox
        >
        <b-form-checkbox v-model="allowGuestSource" id="allow-guest-checkbox"
          >ゲストにも閲覧を許可する</b-form-checkbox
        >
        <b-form-checkbox
          v-if="hasFeature('deny-edit')"
          v-model="allowWriteOthersSource"
          id="allow-write-others"
          >作成者以外の投稿(ゲストを除く)・コメント・いいねを許可する</b-form-checkbox
        >
      </b-form-group>
    </b-container>
  </b-container>
</template>
<style type="scss" scoped></style>
<script lang="ts">
import {
  computed,
  defineComponent,
  getCurrentInstance,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
  type PropType,
} from "vue";
import type Vue from "vue";
import { useAppStore, useCategoryStore, useTopicStore } from "@/store";
import NavigationBar from "./NavigationBar.vue";
import CategoryEdit from "./CategoryEdit.vue";
import type { Attachment } from "../model";
import { Category, Topic, User, UNDEFINED_CATEGORY, AttachmentFactory } from "../model";
import FileSelectionForm from "./FileSelectionForm.vue";
import { COLOR_PALETTE, DEFAULT_COLOR } from "./color-palette";
import Confirm from "./button/Confirm.vue";
import Cancel from "./button/Cancel.vue";
import { useTextProcess } from "@/composables/useTextProcess";
import { Route, NavigationGuardNext } from "vue-router";
import { useEditDiscard } from "@/composables/useEditDiscard";
import { useLegacyRootEvent } from "@/composables/useLegacyRootEvent";
import DragAreaOverlay from "./DropAreaOverlay.vue";
import Acl from "../model/acl";
import { allowCreateTopic, allowFeature, FeatureName } from "../direct-app-config";

import "./topic-edit.scss";
import AclManager from "../model/acl-manager";
import { v4 as uuidv4 } from "uuid";
import { clone } from "lodash";
import S3AccessUtility from "./s3-acceess-utility";

// 制限値
// const TOPIC_TITLE_MAX_LENGTH = 100; // タイトル文字数100文字まで → titleMaxLength に移動
const TOPIC_DESC_MAX_LENGTH = 500;

type PrevData = {
  title: string;
  desc: string;
  photos: Attachment[];
  category: Category | null;
  pinned: boolean;
  acl: Acl;
  pinnedSourceOptions: { notification: boolean };
};

export default defineComponent({
  name: "topic-edit",
  components: {
    NavigationBar,
    CategoryEdit,
    FileSelectionForm,
    Confirm,
    Cancel,
    DragAreaOverlay,
  },
  props: {
    /** create: 新規作成 edit: 編集 */
    type: { type: String, default: () => "edit" },

    // 組織IDは偽装される場合があるためここでは設定しない
    // domainId: { type: String, required: true },

    // 初期データ：カテゴリー一覧
    categories: { type: Array as PropType<Category[]>, default: () => [] },

    // 編集時の組織ID。未指定時は新規追加
    topicId: { type: String, default: () => undefined },
    domainId: { type: String, default: () => "" },

    /** タイトル */
    title: { type: String, default: () => "" },

    /** 説明 */
    desc: { type: String, default: () => "" },

    /** サムネイル */
    thumb: { type: Object as PropType<File | Attachment>, default: () => undefined },

    /** カテゴリー */
    category: { type: Object as PropType<Category>, default: () => null },

    /** 固定 */
    pinned: { type: Boolean, default: () => false },

    acl: { type: Object as PropType<Acl>, default: () => Acl.createDummy() },

    // Direct組織設定
    // 元 @Prop は runtime type 引数なし（型注釈 number のみ・default true）。検証を増やさないため type は付けない。
    allow_attachment_type: { default: () => true as unknown as number },

    // Storbook用(ダイアログの自動表示)
    show: { type: Boolean, default: () => false },

    isMobile: { type: Boolean, default: () => false },

    // ログインユーザー情報
    me: { type: Object as PropType<User>, default: () => User.createNotFoundUser() },
  },
  setup(props) {
    // 旧 TextProcessMixin / EditDiscardMixin → composable（1-D 利用側書き換え）
    const { getLength } = useTextProcess();
    const { changeEditMode, showEditDiscardAlert, routeLeaveEvent } = useEditDiscard();
    const rootEvent = useLegacyRootEvent();
    // $bvModal / $router は BootstrapVue / vue-router が生やす（Step 3 で composable 化予定）
    const vm = getCurrentInstance()?.proxy as Vue | undefined;

    // ナビゲーションバーの設定
    const items = ref([
      { label: "保存", click: (): Promise<void> => onEditComplete(), disabled: false },
    ]);

    const titleSource = ref<string>(""); // タイトル
    const descSource = ref<string>(""); // 説明
    const thumbSource = ref<(File | Attachment)[]>([]); // サムネ(file-selection-formのfilesと同期させる)
    const categorySource = ref<Category | null>(null); // 現在選択中のカテゴリー
    const pinnedSource = ref<boolean>(false); // 固定
    const pinnedSourceOptions = ref<{ notification: boolean }>({ notification: false });
    const notifyOnCreate = ref<boolean>(false); // 話題作成時に通知するか

    // ACL
    const allowGuestSource = ref<boolean>(true); // true: ゲストの閲覧許可
    const allowWriteOthersSource = ref<boolean>(true); // true: 作成者以外が変更できる

    const replaceParam = ref<{ domainId: string; topicId: string }>({ domainId: "", topicId: "" }); // router.replace用パラメータ

    const prevData = ref<PrevData>({
      title: "",
      desc: "",
      photos: [],
      category: null,
      pinned: false,
      acl: Acl.createDummy(),
      pinnedSourceOptions: { notification: false },
    });

    const submitLock = ref<boolean>(false);
    const drag = ref<boolean>(false);

    const topicIdByCreate = ref<string>(""); // 新規作成時にあらかじめ作るtopicId

    // Vuexの変更監視
    let unsubscribeMutation: (() => void) | undefined;

    // $refs.fileSelectionForm
    const fileSelectionForm = ref<FileSelectionForm | null>(null);

    // @Watch(immediate) 群は setup 末尾（全 const / computed / 関数定義後）で登録する。
    // immediate watch は watch() 呼び出し時に同期発火するため、saveTmpData / photos など
    // 後方で定義される const を参照すると TDZ エラーになる。登録位置を末尾に移して回避。

    // 添付可能か
    const allow_attachment = computed((): boolean => {
      return props.allow_attachment_type > 0;
    });

    const isNew = computed((): boolean => {
      return props.topicId == undefined;
    });

    const navTitle = computed((): string => {
      return isNew.value ? "話題の作成" : "話題の編集";
    });

    const selectedCategoryTitle = computed((): string => {
      if (categorySource.value) return categorySource.value.title;
      return "カテゴリーを選択";
    });

    const selectedCategoryColor = computed((): string => {
      // カテゴリー未選択
      if (!categorySource.value) return "";

      const color = categorySource.value.color;
      const index = COLOR_PALETTE.findIndex((c) => c == color);
      return index < 0 ? DEFAULT_COLOR : color;
    });

    const completeButtonDisabled = computed((): boolean => {
      if (AclManager.createTopic(AclManager.store, props.domainId) == false) return false; // 書き込み権無し
      return !validateTitle.value || !validateDesc.value || !categorySource.value?.title;
    });

    const categoryErrorMsg = computed((): string => {
      return categorySource.value != null
        ? `カテゴリー「${categorySource.value.title}」は削除されています`
        : "カテゴリーが未選択です";
    });

    const photos = computed((): Attachment[] => {
      return props.thumb ? [props.thumb as Attachment] : [];
    });

    // タイトルのvalidate
    const validateTitle = computed((): boolean => {
      if (!titleSource.value) return false;
      const tmp = titleSource.value.trim();
      return 0 < getLength(tmp) && getLength(tmp) <= titleMaxLength();
    });

    const validTitleMessage = computed((): string => {
      const tmp = titleSource.value.trim();
      const titleMaxLengthValue = titleMaxLength();
      return getLength(tmp)
        ? `タイトルは${titleMaxLengthValue}文字以内です(${getLength(tmp)}/${titleMaxLengthValue})`
        : "タイトルは必須項目です";
    });

    // 詳細のvalidate
    const validateDesc = computed((): boolean => {
      if (!descSource.value) return false;
      const tmp = descSource.value.trim().replace(/\r?\n/g, "");
      return 0 < getLength(tmp) && getLength(tmp) <= TOPIC_DESC_MAX_LENGTH;
    });

    const validDescMessage = computed((): string => {
      const tmp = descSource.value.trim().replace(/\r?\n/g, "");
      return getLength(tmp)
        ? `説明は${TOPIC_DESC_MAX_LENGTH}文字以内です(${getLength(tmp)}/${TOPIC_DESC_MAX_LENGTH})`
        : "説明は必須項目です";
    });

    // カテゴリーのvalidate
    const validateCategory = computed((): boolean => {
      return 0 < (categorySource.value?.title || "").length;
    });

    // true: 変更点が有る
    const editMode = computed((): boolean => {
      if (titleSource.value != prevData.value.title) return true;
      if (descSource.value != prevData.value.desc) return true;
      if (JSON.stringify(thumbSource.value) != JSON.stringify(prevData.value.photos)) return true;
      if (categorySource.value != prevData.value.category) return true;
      if (notifyOnCreate.value != false) return true;
      if (pinnedSource.value != prevData.value.pinned) return true;
      if (pinnedSourceOptions.value.notification != prevData.value.pinnedSourceOptions.notification)
        return true;
      // acl
      if (allowGuestSource.value != prevData.value.acl.guest.read) return true;
      if (allowWriteOthersSource.value == true && prevData.value.acl.base.createMessage != "allow")
        return true;
      if (allowWriteOthersSource.value == false && prevData.value.acl.base.createMessage != "deny")
        return true;
      /** acl.itemsの編集記録は編集モーダルを閉じても残っているため判定は行わない */

      return false;
    });

    /** @return true: ログインユーザが管理者ロール以上 */
    const isAdmin = computed((): boolean => {
      return props.me.isAdminRole(props.domainId) || props.me.isOwnerRole(props.domainId);
    });

    /** 閲覧権限設定の有無 */
    const aclStateStr = computed((): string => {
      const topicId = props.topicId || topicIdByCreate.value;
      const storeAcl = useAppStore().getAclData(topicId) as Acl;
      const aclItems = storeAcl ? storeAcl.items : props.acl.items;
      return `設定：${0 < aclItems.length ? "あり" : "なし"}`;
    });

    // prevDataに一時的にデータ保存する
    const saveTmpData = (): void => {
      prevData.value = {
        title: props.title,
        desc: props.desc,
        photos: photos.value,
        category: props.category,
        pinned: props.pinned,
        acl: props.acl.clone(),
        pinnedSourceOptions: clone(pinnedSourceOptions.value),
      };
    };

    const resetParam = () => {
      // パラメータの初期化
      titleSource.value = "";
      descSource.value = "";
      thumbSource.value = [];
      categorySource.value = null;
      notifyOnCreate.value = false;
      pinnedSource.value = false;
      pinnedSourceOptions.value = { notification: false };
      allowGuestSource.value = true;
      allowWriteOthersSource.value = true;
      topicIdByCreate.value = Topic.createId();
      submitLock.value = false;
    };

    const dropFiles = (files: FileList): void => {
      const form = fileSelectionForm.value as FileSelectionForm;
      form.dropFiles(files);
    };

    const updateFile = (files: (File | Attachment)[]): void => {
      thumbSource.value = files;
      changeEditMode(editMode.value);
    };

    const updateComplteButtonDisabled = (): void => {
      items.value[0].disabled = !(
        validateTitle.value &&
        validateDesc.value &&
        validateCategory.value
      );
    };

    const onChangePinnedSource = (): void => {
      // 固定ピンがオフになった場合はそのoptionも全てオフ
      if (!pinnedSource.value) {
        pinnedSourceOptions.value.notification = false;
      }
    };

    // カテゴリ選択
    const onCategorySelected = (categories: Category[]): void => {
      if (0 < categories.length) {
        categorySource.value = categories[0];
        updateComplteButtonDisabled();
      }
      changeEditMode(editMode.value);
    };

    // 選択されたカテゴリーが存在するか(削除されていないか)
    const existsSelectedCategory = (): boolean => {
      const categories = useCategoryStore().get(props.domainId, false) as Category[]; // 削除されたものも含めてカテゴリー取得
      const category = categories.find(
        (c) => categorySource.value != null && c.id == categorySource.value.id
      );
      return Category.isExists(category);
    };

    // キャンセルボタンが押されたときの処理
    const onEditCancel = (): void => {
      closeModal();
    };

    // 完了ボタンが押された時の処理
    const onEditComplete = async (): Promise<void> => {
      // 送信連打による連続送信を防止
      if (submitLock.value) return;
      submitLock.value = true;

      // カテゴリーが削除された場合(またはカテゴリーが未選択)の場合にエラー
      if (!existsSelectedCategory()) {
        rootEvent.emit("show-error-modal", {
          msg: categoryErrorMsg.value,
          afterProcess: () => {
            return;
          },
        });
        submitLock.value = false;
        return;
      }

      // 新規作成前に作成上限を超えている場合はアラート
      if (isNew.value && allowCreateTopic(props.domainId) == false) {
        rootEvent.emit("free-alert");
        submitLock.value = false;
        return;
      }

      const topicId = props.topicId || topicIdByCreate.value;
      const s3AccessUtility = S3AccessUtility.getInstance();
      let file = null;
      if (!thumbSource.value.length) {
        // 削除
        await s3AccessUtility.deleteFiles(prevData.value.photos, {
          domainId: props.domainId,
          topicId: topicId,
        });
      } else {
        // 追加
        if (
          JSON.stringify(thumbSource.value) != JSON.stringify(prevData.value.photos) &&
          thumbSource.value.length === 1 &&
          !AttachmentFactory.isAttachment(thumbSource.value[0])
        ) {
          let files: Attachment[] = [];
          const generator = async () => {
            let returnValue: boolean = true;
            for await (const result of s3AccessUtility.uploadFilesGenerator(thumbSource.value, {
              domainId: props.domainId,
              topicId: topicId,
            })) {
              if (!result) {
                returnValue = false;
                break;
              }

              files = result.attachments;
              if (result.done) {
                returnValue = true;
                break;
              }
            }
            return returnValue;
          };

          const result = await generator();
          if (!result) {
            rootEvent.emit("show-error-modal", {
              msg: "添付ファイルの送信に失敗しました。",
              afterProcess: () => {
                return;
              },
            });
            return;
          }
          file = files[0];
        }
      }

      /* ACLの更新 */
      const storeAcl = useAppStore().getAclData(topicId) as Acl;
      const aclItems = storeAcl ? storeAcl.items : props.acl.items;
      const acl = Acl.createByTopic(
        topicId,
        allowGuestSource.value,
        allowWriteOthersSource.value,
        aclItems
      );
      useAppStore().setAclData({ id: props.topicId, acl: acl }); // ゲスト/書き込み権も含めたACLに更新

      const notificationId = getNotificationId();
      const param: Partial<Topic> = {
        id: topicId,
        domainId: props.domainId,
        title: titleSource.value,
        desc: descSource.value,
        icon: file,
        category: categorySource.value || UNDEFINED_CATEGORY,
        pinned: pinnedSource.value,
        acl: acl,
        notification: notificationId,
      };
      const topic = isNew.value ? Topic.createTemp(param) : param;
      const call = isNew.value ? "on-topic-create" : "on-topic-update";
      const obj = isNew.value ? { topic, notifyOnCreate: notifyOnCreate.value } : { param: topic };
      if (vm?.$root && vm.$root.$emit) {
        rootEvent.emit(call, obj);
      }

      changeEditMode(false);
      // 完了後は閉じる
      closeModal();
    };

    // close Event
    const closeModal = (): void => {
      if (vm?.$router && props.isMobile) {
        vm.$router.back();
      } else if (!props.isMobile) {
        vm?.$bvModal.hide("modal-topic");
      }
      if (props.topicId) {
        // 編集したaclのリセット
        useAppStore().setAclData({ id: props.topicId, acl: undefined });
      }
    };

    // 削除後のイベント
    const afterDeleteProcess = (): void => {
      vm?.$router.replace(`/${replaceParam.value.domainId}`);
    };

    // Feature
    const hasFeature = (keyword: FeatureName): boolean => {
      return allowFeature(keyword);
    };
    const titleMaxLength = (): number => {
      return hasFeature("topic-title-long") ? 100 : 20;
    };

    const openAclEditModal = (): void => {
      const topicId = props.topicId || topicIdByCreate.value;
      const storeAcl = useAppStore().getAclData(topicId);
      if (!storeAcl) {
        useAppStore().setAclData({ id: topicId, acl: props.acl });
      }
      rootEvent.emit("open-acl-modal", {
        id: topicId,
        type: "TOPIC",
        acl: useAppStore().getAclData(topicId),
      });
    };

    // 話題のnotification取得
    const getNotificationId = (): string | undefined => {
      const notification = pinnedSourceOptions.value.notification;
      // 全体通知は行わない
      if (!notification) {
        return undefined;
      }
      // 新規投稿: notificationId作成
      else if (isNew.value) {
        return uuidv4();
      }
      // 既に設定済みの場合は、そのID
      else {
        const topic = useTopicStore().getOne(props.domainId, props.topicId);
        if (topic) {
          return topic.notification ? topic.notification : uuidv4();
        } else {
          return undefined;
        }
      }
    };

    // Lifecycle
    onBeforeUnmount(() => {
      if (unsubscribeMutation) unsubscribeMutation();
    });

    // created 相当
    updateComplteButtonDisabled();

    onMounted(() => {
      if (props.show) {
        vm?.$bvModal.show("modal-topic");
      }

      // 旧 Vuex の topics/delete mutation 監視を state 変化監視（$subscribe）へ移植。
      // 当該 mutation は action / GraphQL サブスクリプション双方から発火していたため $onAction では捕捉しきれない。
      const topicStore = useTopicStore();
      let topicWasPresent = !!props.topicId && !!topicStore.getOne(props.domainId, props.topicId);
      unsubscribeMutation = topicStore.$subscribe(() => {
        if (!props.topicId) return;
        const present = !!topicStore.getOne(props.domainId, props.topicId);
        // 開いている話題が存在 -> 非存在に変化した = 削除された -> 話題一覧に移動
        if (topicWasPresent && !present) {
          const domainId = props.domainId;
          const topicId = props.topicId;
          if (topicId) {
            replaceParam.value = { domainId: domainId, topicId: topicId };
          }
          rootEvent.emit("show-error-modal", {
            msg: "話題が削除されたため、話題一覧に移動します",
            afterProcess: () => afterDeleteProcess(),
          });
        }
        topicWasPresent = present;
      });
    });

    // @Watch(immediate) 群（全 const / computed / 関数定義後に登録: TDZ 回避）
    watch(
      () => props.title,
      (value: string): void => {
        titleSource.value = value;
        saveTmpData();
      },
      { immediate: true }
    );

    watch(
      () => props.desc,
      (value: string): void => {
        descSource.value = value;
        saveTmpData();
      },
      { immediate: true }
    );

    watch(
      () => props.thumb,
      (): void => {
        thumbSource.value = photos.value.slice();
        saveTmpData();
      },
      { immediate: true }
    );

    watch(
      () => props.category,
      (value: Category): void => {
        categorySource.value = value;
        saveTmpData();
      },
      { immediate: true }
    );

    watch(
      () => props.pinned,
      (value: boolean): void => {
        pinnedSource.value = value;
        // 固定オプション
        if (pinnedSource.value) {
          const topic = useTopicStore().getOne(props.domainId, props.topicId);
          if (topic) pinnedSourceOptions.value.notification = !!topic.notification;
        }
        saveTmpData();
      },
      { immediate: true }
    );

    watch(
      () => props.acl,
      (value: Acl): void => {
        allowGuestSource.value = value.readTopicByGuest();
        allowWriteOthersSource.value = value.createMessageByOthers();
        saveTmpData();
      },
      { immediate: true }
    );

    return {
      items,
      titleSource,
      descSource,
      thumbSource,
      categorySource,
      pinnedSource,
      pinnedSourceOptions,
      notifyOnCreate,
      allowGuestSource,
      allowWriteOthersSource,
      replaceParam,
      prevData,
      submitLock,
      drag,
      topicIdByCreate,
      fileSelectionForm,
      changeEditMode,
      showEditDiscardAlert,
      routeLeaveEvent,
      allow_attachment,
      isNew,
      navTitle,
      selectedCategoryTitle,
      selectedCategoryColor,
      completeButtonDisabled,
      categoryErrorMsg,
      photos,
      validateTitle,
      validTitleMessage,
      validateDesc,
      validDescMessage,
      validateCategory,
      editMode,
      isAdmin,
      aclStateStr,
      saveTmpData,
      resetParam,
      dropFiles,
      updateFile,
      updateComplteButtonDisabled,
      onChangePinnedSource,
      onCategorySelected,
      existsSelectedCategory,
      onEditCancel,
      onEditComplete,
      closeModal,
      afterDeleteProcess,
      hasFeature,
      titleMaxLength,
      openAclEditModal,
      getNotificationId,
      allowFeature,
    };
  },
  beforeRouteEnter(to: Route, from: Route, next: NavigationGuardNext): void {
    // 編集画面でリロードされた場合、話題ページへリダイレクト
    if (from.name) {
      next();
    } else {
      const domainId = to.params.domainId;
      console.log(`* redirect: /${domainId}`);
      next(`/${domainId}`);
    }
  },
  beforeRouteLeave(to: Route, from: Route, next: NavigationGuardNext): void {
    this.routeLeaveEvent(next);
  },
});
</script>
